/**
 * Audio event interfaces for the Sharpee media subsystem.
 *
 * Public interface: All audio event shapes, the AudioEvent discriminated
 * union, and the isAudioEvent() type guard.
 *
 * Owner context: @sharpee/media (ADR-138). These events flow through the
 * engine's event pipeline unchanged — clients that support audio render
 * them; others ignore them.
 */

import type {
  Volume,
  DurationMs,
  StereoPan,
  PlaybackRate,
  AudioAssetPath,
  AmbientChannel,
  DuckPriority,
  AudioTarget,
  AudioEffectType,
  ProceduralRecipeName,
} from './types';

/**
 * Base interface for all audio events.
 * Audio events flow through the engine's event pipeline unchanged.
 * Clients that support audio render them; others ignore them.
 */
export interface AudioEventBase {
  readonly type: string;
}

// ── Sound Effects (One-Shot) ──────────────────────────────────────────
// Short sounds triggered by actions or state changes.
// Fire-and-forget: play once and discard.

/**
 * Play a sound effect.
 *
 * Use cases: door opening, item pickup, skin activation, weapon fire,
 * system alert, puzzle solve chime.
 */
export interface AudioSfxEvent extends AudioEventBase {
  readonly type: 'audio.sfx';

  /** Audio file path (relative to assets/audio/) */
  readonly src: AudioAssetPath;

  /** Playback volume. Default: 1.0 */
  readonly volume?: Volume;

  /** Playback speed. Default: 1.0 */
  readonly rate?: PlaybackRate;

  /** Stereo position. Default: 0.0 (center) */
  readonly pan?: StereoPan;

  /**
   * Ducking priority. Higher values duck lower-priority audio.
   * When this SFX fires, ambient and music temporarily reduce volume
   * so the SFX cuts through the mix. Default: 0 (no ducking).
   *
   * Suggested values:
   * - 0: ambient/background SFX (no ducking)
   * - 1: normal gameplay SFX (subtle duck)
   * - 2: important feedback (moderate duck)
   * - 3: critical alerts, combat hits (aggressive duck)
   */
  readonly duck?: DuckPriority;
}

// ── Music (Single Track) ──────────────────────────────────────────────
// Background music. Only one music track plays at a time.
// Starting a new track crossfades from the current one.

/**
 * Start playing a music track, or crossfade to a new one.
 * If music is already playing, the current track fades out while
 * the new one fades in over the fadeIn duration.
 *
 * Use cases: exploration theme, tension/combat music, victory stinger.
 */
export interface AudioMusicPlayEvent extends AudioEventBase {
  readonly type: 'audio.music.play';

  /** Audio file path (relative to assets/audio/) */
  readonly src: AudioAssetPath;

  /** Playback volume. Default: 0.5 */
  readonly volume?: Volume;

  /** Fade-in duration. Default: 1000ms */
  readonly fadeIn?: DurationMs;

  /** Whether to loop the track. Default: true */
  readonly loop?: boolean;
}

/**
 * Stop the current music track.
 */
export interface AudioMusicStopEvent extends AudioEventBase {
  readonly type: 'audio.music.stop';

  /** Fade-out duration. Default: 1000ms */
  readonly fadeOut?: DurationMs;
}

// ── Ambient (Layered Loops) ───────────────────────────────────────────
// Environmental atmosphere. Multiple named channels play simultaneously,
// each independently controllable. Starting a channel that is already
// playing replaces it with a crossfade.

/**
 * Start or replace an ambient channel.
 *
 * Use cases: wind, rain, cave dripping, engine hum, distant explosions,
 * electrical buzz, crowd murmur.
 */
export interface AudioAmbientPlayEvent extends AudioEventBase {
  readonly type: 'audio.ambient.play';

  /** Audio file path (relative to assets/audio/) */
  readonly src: AudioAssetPath;

  /** Named channel. Reusing a channel name replaces the current source. */
  readonly channel: AmbientChannel;

  /** Playback volume. Default: 0.3 */
  readonly volume?: Volume;

  /** Fade-in duration. Default: 2000ms */
  readonly fadeIn?: DurationMs;

  /** Whether to loop. Default: true */
  readonly loop?: boolean;
}

/**
 * Stop a single ambient channel.
 */
export interface AudioAmbientStopEvent extends AudioEventBase {
  readonly type: 'audio.ambient.stop';

  /** Channel to stop */
  readonly channel: AmbientChannel;

  /** Fade-out duration. Default: 2000ms */
  readonly fadeOut?: DurationMs;
}

/**
 * Stop all ambient channels at once (e.g., on scene change).
 */
export interface AudioAmbientStopAllEvent extends AudioEventBase {
  readonly type: 'audio.ambient.stop_all';

  /** Fade-out duration for all channels. Default: 1000ms */
  readonly fadeOut?: DurationMs;
}

// ── Procedural Audio ──────────────────────────────────────────────────
// Synthesized sounds generated by the client using Web Audio oscillators,
// noise generators, and filters. No audio files required.

/**
 * Play a procedural sound from a named recipe.
 *
 * Recipes are registered by the client, not defined by the story.
 * The story says *what* conceptual sound it wants; the client decides
 * how to synthesize it. Clients without procedural support can map
 * recipe names to fallback audio files, or skip the event.
 *
 * Use cases: system beeps, alerts, electrical hum, static bursts,
 * tonal feedback for puzzle state.
 */
export interface AudioProceduralEvent extends AudioEventBase {
  readonly type: 'audio.procedural';

  /** Named recipe identifier */
  readonly recipe: ProceduralRecipeName;

  /** Recipe-specific parameters (override recipe defaults) */
  readonly params?: Readonly<Record<string, number>>;

  /** Playback volume. Default: 1.0 */
  readonly volume?: Volume;

  /** Duration override. Uses recipe default if omitted. */
  readonly duration?: DurationMs;

  /** Ducking priority. See AudioSfxEvent.duck. Default: 0 */
  readonly duck?: DuckPriority;
}

// ── Audio Effects ─────────────────────────────────────────────────────
// Per-channel audio processing. Clients MAY support effects — stories
// request them as hints and degrade gracefully if unsupported.

/**
 * Apply an audio effect to a mix target.
 *
 * Use cases: reverb in caves, muffled sound behind walls,
 * distortion during damage, lowpass for flashback sequences.
 */
export interface AudioEffectEvent extends AudioEventBase {
  readonly type: 'audio.effect';

  /** Which part of the audio mix to affect */
  readonly target: AudioTarget;

  /** Effect to apply */
  readonly effect: AudioEffectType;

  /** Effect-specific parameters */
  readonly params: Readonly<Record<string, number>>;

  /** Transition time to reach new effect state. Default: 0 (instant) */
  readonly transition?: DurationMs;
}

/**
 * Remove all effects from a mix target.
 */
export interface AudioEffectClearEvent extends AudioEventBase {
  readonly type: 'audio.effect.clear';

  /** Which part of the audio mix to clear effects from */
  readonly target: AudioTarget;

  /** Transition time to remove effects. Default: 0 (instant) */
  readonly transition?: DurationMs;
}

// ── Discriminated Union ───────────────────────────────────────────────

/**
 * Union of all audio events. Clients switch on the `type` discriminant.
 */
export type AudioEvent =
  | AudioSfxEvent
  | AudioMusicPlayEvent
  | AudioMusicStopEvent
  | AudioAmbientPlayEvent
  | AudioAmbientStopEvent
  | AudioAmbientStopAllEvent
  | AudioProceduralEvent
  | AudioEffectEvent
  | AudioEffectClearEvent;

// ── Type Guards ───────────────────────────────────────────────────────

/**
 * Returns true if the event is any audio event.
 *
 * @param event - Any object with a `type` string field
 * @returns Type predicate narrowing to AudioEvent
 */
export function isAudioEvent(event: { type: string }): event is AudioEvent {
  return event.type.startsWith('audio.');
}
